import React from "react";
import { Link } from "react-router-dom";
import { Lightbulb, X as XIcon, TrendingUp } from "lucide-react";
import { gbp } from "../../lib/apiClient";

const median = (sorted) => {
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * SimilarQuoteBanner — orange callout showing past quotes that match the current
 * category (and optionally the exact set of repair parts).
 */
export function SimilarQuoteBanner({ category, partsCount = 0, matches, onDismiss }) {
  const partsClause = partsCount > 0 ? ` with the same ${partsCount} part${partsCount === 1 ? "" : "s"}` : "";
  const shown = matches.slice(0, 5);
  const totals = shown.map((m) => Number(m.grand_total || 0)).filter((n) => n > 0).sort((a, b) => a - b);
  const avg = totals.length ? totals.reduce((a, b) => a + b, 0) / totals.length : 0;
  const minV = totals[0] || 0;
  const maxV = totals[totals.length - 1] || 0;
  const medV = median(totals);
  return (
    <div
      className="qb-card border-l-4 border-brand-orange bg-orange-50/40 fade-up"
      data-testid="similar-quote-banner"
    >
      <div className="p-4 flex items-start gap-3">
        <Lightbulb size={20} className="text-brand-orange mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm font-semibold text-slate-900">
              {matches.length} previous {matches.length === 1 ? "calculation" : "calculations"} found for <span className="text-brand-orange">{category}</span>{partsClause}
            </div>
            {avg > 0 && (
              <span
                className="relative group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-orange text-white text-xs font-semibold cursor-help"
                data-testid="similar-avg-total"
                tabIndex={0}
              >
                Avg of {totals.length}: <span className="font-mono">{gbp(avg)}</span>
                <span
                  role="tooltip"
                  data-testid="similar-avg-popover"
                  className="pointer-events-none absolute z-20 top-full left-1/2 -translate-x-1/2 mt-2 w-56 rounded-lg bg-slate-900 text-white text-[11px] shadow-xl opacity-0 invisible translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0"
                >
                  <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                    <TrendingUp size={12} className="text-brand-orange" />
                    <span className="uppercase tracking-wider font-bold">Spread across {totals.length}</span>
                  </div>
                  <dl className="px-3 py-2 space-y-1.5">
                    <div className="flex items-baseline justify-between gap-3" data-testid="similar-stat-min">
                      <dt className="text-white/60">Min</dt>
                      <dd className="font-mono font-semibold">{gbp(minV)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-3" data-testid="similar-stat-median">
                      <dt className="text-white/60">Median</dt>
                      <dd className="font-mono font-semibold">{gbp(medV)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-3" data-testid="similar-stat-max">
                      <dt className="text-white/60">Max</dt>
                      <dd className="font-mono font-semibold">{gbp(maxV)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-white/10" data-testid="similar-stat-avg">
                      <dt className="text-brand-orange font-semibold">Avg</dt>
                      <dd className="font-mono font-semibold">{gbp(avg)}</dd>
                    </div>
                  </dl>
                </span>
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {partsCount > 0
              ? "These quotes have the EXACT same parts selected — use them as a price reference."
              : "Pick the parts on this job to narrow the match to quotes with the same exact parts."}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {shown.map((m) => (
              <Link
                key={m.id}
                to={`/quotes/${m.id}`}
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:border-brand-orange text-xs text-slate-700 hover:text-brand-orange transition-colors"
                data-testid={`similar-quote-${m.id}`}
                title={m.description}
              >
                <span className="font-mono font-semibold text-slate-900">{m.quote_number}</span>
                <span className="truncate max-w-[160px]">{m.customer_name}</span>
                <span className="font-mono text-[10px] text-slate-500">{gbp(m.grand_total || 0)}</span>
              </Link>
            ))}
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-700 flex-shrink-0"
          aria-label="Dismiss suggestions"
          data-testid="similar-dismiss"
        >
          <XIcon size={16} />
        </button>
      </div>
    </div>
  );
}
